import {
  makeStyles, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography,
} from '@material-ui/core';
import React from 'react';
import { LabelProps, ControlProps } from './controlProps';
import ErrorText from './ErrorText';

const useStyles = makeStyles(() => ({
  label: {
    alignItems: 'flex-start',
  },
}));

interface CustomLabelProps extends LabelProps {
  fontSize?: string;
}

function CustomLabel(props: CustomLabelProps): JSX.Element {
  const { title, subtitle, fontSize } = props;
  const padding = subtitle ? '12px' : '0px';
  return (
    <div style={{ paddingBottom: padding }}>
      <Typography style={{ fontWeight: 'bold', fontSize: fontSize || '0.9em' }}>{title}</Typography>
      <Typography style={{ fontSize: '0.85em', whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: subtitle }} />
    </div>
  );
}

CustomLabel.defaultProps = {
  fontSize: '1em',
};

export default function AsaClassControl(props: ControlProps) : JSX.Element {
  const { value, error, handleChange } = props;
  const classes = useStyles();

  return (
    <FormControl component="fieldset" id="asa-class">
      <FormLabel component="legend" style={{ color: 'black', paddingBottom: '12px' }}>
        <CustomLabel
          title="What is the ASA classification for your patient?"
          subtitle="BMI is considered in the ASA classification and the result from our BMI calculator will help you make this assignment for your patient."
          fontSize="1em"
        />
      </FormLabel>
      <RadioGroup aria-label="asaClass" name="asaClass" value={value} onChange={handleChange}>
        <FormControlLabel
          value="1"
          control={<Radio />}
          label={<CustomLabel title="Class 1 - A normal healthy patient" subtitle="Healthy; non-smoking, no or minimal alcohol use" />}
          className={classes.label}
        />
        <FormControlLabel
          value="2"
          control={<Radio />}
          label={(
            <CustomLabel
              title="Class 2 - A patient with mild systemic disease"
              subtitle={'Examples include: current smoker, social alcohol drinker, <span style="white-space: nowrap">obesity (30kg/m² < BMI < 40kg/m²)</span>, <span style="white-space: nowrap">well-controlled DM/HTN</span>, <span style="white-space: nowrap">mild chronic lung disease</span>'}
            />
          )}
          className={classes.label}
        />
        <FormControlLabel
          value="3"
          control={<Radio />}
          label={(
            <CustomLabel
              title="Class 3 - A patient with severe systemic disease"
              subtitle={'Substantive functional limitations; one or more moderate to severe diseases. Examples include: poorly controlled DM/HTN, COPD, <span style="white-space: nowrap">morbid obesity (BMI &ge; 40kg/m²)</span>, active liver disease, alcohol dependence, <span style="white-space: nowrap">history (>3 months) of MI, CVA, TIA or CAD/stents</span>'}
            />
          )}
          className={classes.label}
        />
        <FormControlLabel
          value="4"
          control={<Radio />}
          label={(
            <CustomLabel
              title="Class 4 - A patient with severe systemic disease that is a constant threat to life"
              subtitle="Examples include: recent (<3 months) MI, CVA, TIA or CAD/stents, ongoing cardiac ischemia or severe cardiac dysfunction"
            />
          )}
          className={classes.label}
        />
        <FormControlLabel
          value="5"
          control={<Radio />}
          label={<CustomLabel title="Class 5 - A moribund patient who is not expected to survive without the operation" subtitle="" />}
          className={classes.label}
        />
      </RadioGroup>
      <ErrorText error={error || false} errorText="You must select an option" />
    </FormControl>
  );
}
